// VIS-267: the CPU copy of what the graphics chip already holds goes, with no look change (phones count both in their
// 230-280 MB). Nothing in three uploads from it again (a lost device reloads the page onto the rescue set), so:
//  - releaseCpu swaps an uploaded attribute's array for a stub of the same type (one item): `count` is a plain property
//    and draws read it; the stub's constructor still names the vertex / index format for a pipeline built later. The
//    version is never bumped (that would send the stub). Bounds are computed first (culling and Box3 read them later);
//  - freeOnUpload drops a texture's picture once three has uploaded it (texture.onUpdate, main.js freeImage's way).
// The caller keeps what is read later (`keep`: raycasts read position + index) and passes only geometry it never
// rewrites. Never Points/Lines, skinned or batched (three reads their arrays for bounds), nor non-static, storage,
// instanced or interleaved attributes. A released geometry is never disposed and drawn again: that re-uploads the stub.
import * as THREE from 'three/webgpu';
import { uploaded } from './internals.js';

let freed = 0, pictures = false;
/** main.js: a visitor's page (config.js RELEASE_CPU) lets freeOnUpload drop pictures; the editor's never does. */
export const releasePictures = (on) => { pictures = !!on; };
/** bytes released this visit (`&perf` / debug) */
export const releasedBytes = () => freed;

const done = new WeakSet(); // attributes already a stub (one may sit in several geometries)
const fixed = (a) => !!a?.array && !a.isInterleavedBufferAttribute && !a.isInstancedBufferAttribute && !a.isStorageBufferAttribute
  && !a.isStorageInstancedBufferAttribute && !a.isIndirectStorageBufferAttribute && !a.isGLBufferAttribute && a.usage === THREE.StaticDrawUsage; // (any other usage is rewritten)

/** Release the CPU arrays of `geometry` (or a mesh's) that are on the graphics chip, all but `keep` (attribute names,
 *  'index' for the index). One not uploaded yet (a pass that never drew it) stays and a later call takes it; once
 *  nothing is left `userData.cpuReleased` is true and further calls do nothing. Returns the bytes freed. */
export function releaseCpu(renderer, geometry, { keep = [] } = {}) {
  const o = geometry?.isObject3D ? geometry : null, g = o ? o.geometry : geometry;
  if (!g?.isBufferGeometry || g.userData.cpuReleased || g.userData.keepCpu) return 0;
  if (o && (o.isPoints || o.isLine || o.isSkinnedMesh || o.isBatchedMesh)) return 0;
  const parts = Object.entries(g.attributes);
  if (g.index) parts.push(['index', g.index]);
  let bytes = 0, left = 0;
  for (const [name, a] of parts) {
    if (keep.includes(name) || !fixed(a) || done.has(a)) continue;
    if (!uploaded(renderer, a)) { left++; continue; }
    if (g.attributes.position && !done.has(g.attributes.position)) { if (!g.boundingBox) g.computeBoundingBox(); if (!g.boundingSphere) g.computeBoundingSphere(); }
    const stub = new a.array.constructor(a.itemSize); // (three r187 uploads Uint16 indices as Uint16 on WebGPU too, #34734)
    bytes += a.array.byteLength - stub.byteLength;
    a.array = stub;
    done.add(a);
  }
  if (!left) g.userData.cpuReleased = true;
  freed += bytes;
  return bytes;
}

/** Drop the texture's picture once three has uploaded it, on a visitor's page (releasePictures): an ImageBitmap is closed, a data texture's array and a
 *  compressed texture's mip data let go (the size stays). For a texture nothing uploads again. Returns the texture. */
export function freeOnUpload(t) {
  if (!t) return t;
  const own = t.onUpdate;
  t.onUpdate = (x) => {
    t.onUpdate = own ?? null;
    own?.(x);
    if (!pictures) return;
    const im = t.image;
    if (typeof im?.close === 'function') im.close();
    else if (t.isCompressedTexture || t.isCompressedArrayTexture) t.mipmaps = (t.mipmaps ?? []).map((m) => ({ ...m, data: null })); // (new level objects: a clone made before the upload keeps its own)
    else if (im?.data) t.image = { data: null, width: im.width, height: im.height, ...(im.depth !== undefined && { depth: im.depth }) }; // (ground.js setLite's way)
  };
  return t;
}
